import { z } from 'zod';
import { ClaimableTypeSchema, ExpenseCategorySchema, ExpenseTypeSchema, } from './expenses.js';
import { InvoiceTaxTypeSchema } from './invoices.js';
import { RecurrenceFrequencySchema } from './recurrence.js';
// #region Enums
export const RecurringExpenseStatusSchema = z.enum([
    'ACTIVE',
    'PAUSED',
    'COMPLETED',
    'CANCELLED',
]);
export const RecurringExpenseRunStatusSchema = z.enum([
    'PENDING',
    'RETRYING',
    'SUCCEEDED',
    'FAILED',
]);
// #endregion
// #region Shared schema bases
const CostFieldsSchema = z.object({
    title: z.string().min(1),
    totalCost: z.number().positive(),
    taxIncluded: z.boolean().default(false),
    taxType: InvoiceTaxTypeSchema.default('NONE'),
    taxRate: z.number().min(0).max(1).default(0),
    claimableAmount: z.number().min(0),
    claimableAmountType: ClaimableTypeSchema,
    description: z.string().nullable().optional(),
});
const scheduleFields = {
    frequency: RecurrenceFrequencySchema,
    interval: z.number().int().min(1).default(1),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime().optional(),
    maxOccurrences: z.number().int().min(1).optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    isLastDayOfMonth: z.boolean().default(false),
    daysOfWeek: z.array(z.number().int().min(0).max(6)).default([]),
    monthOfYear: z.number().int().min(1).max(12).optional(),
};
// #endregion
// #region Create schema
/** Request body for creating a BUSINESS recurring expense. */
export const CreateRecurringBusinessExpenseSchema = CostFieldsSchema.extend({
    ...scheduleFields,
    type: z.literal('BUSINESS'),
    category: ExpenseCategorySchema,
    lineItemId: z.string().nullable().optional(),
});
/** Request body for creating a REIMBURSEMENT recurring expense. */
export const CreateRecurringReimbursementExpenseSchema = CostFieldsSchema.extend({
    ...scheduleFields,
    type: z.literal('REIMBURSEMENT'),
    clientId: z.string().min(1),
});
/** Request body for creating a recurring expense — discriminated by type. */
export const CreateRecurringExpenseSchema = z.discriminatedUnion('type', [
    CreateRecurringBusinessExpenseSchema,
    CreateRecurringReimbursementExpenseSchema,
]);
// #endregion
// #region Update schema
/** Partial update for a BUSINESS recurring expense. */
export const UpdateRecurringBusinessExpenseSchema = CreateRecurringBusinessExpenseSchema.partial().extend({ type: z.literal('BUSINESS') });
/** Partial update for a REIMBURSEMENT recurring expense. */
export const UpdateRecurringReimbursementExpenseSchema = CreateRecurringReimbursementExpenseSchema.partial().extend({
    type: z.literal('REIMBURSEMENT'),
});
export const UpdateRecurringExpenseSchema = z.discriminatedUnion('type', [
    UpdateRecurringBusinessExpenseSchema,
    UpdateRecurringReimbursementExpenseSchema,
]);
// #endregion
// #region Response schema
export const RecurringExpenseResponseSchema = z.object({
    id: z.string(),
    rexpNumber: z.number(),
    status: RecurringExpenseStatusSchema,
    type: ExpenseTypeSchema,
    category: ExpenseCategorySchema.nullable(),
    title: z.string(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    lineItemId: z.string().nullable(),
    totalCost: z.number(),
    taxIncluded: z.boolean(),
    taxType: InvoiceTaxTypeSchema,
    taxRate: z.number(),
    claimableAmount: z.number(),
    claimableAmountType: ClaimableTypeSchema,
    description: z.string().nullable(),
    frequency: RecurrenceFrequencySchema,
    interval: z.number(),
    dayOfMonth: z.number().nullable(),
    isLastDayOfMonth: z.boolean(),
    daysOfWeek: z.array(z.number()),
    monthOfYear: z.number().nullable(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime().nullable(),
    maxOccurrences: z.number().nullable(),
    occurrenceCount: z.number(),
    nextRunAt: z.iso.datetime().nullable(),
    lastRunAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// #endregion
// #region Run response schema
export const RecurringExpenseRunResponseSchema = z.object({
    id: z.string(),
    recurringExpenseId: z.string(),
    scheduledFor: z.iso.datetime(),
    status: RecurringExpenseRunStatusSchema,
    attemptCount: z.number(),
    triggeredBy: z.string(),
    startedAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
    nextRetryAt: z.iso.datetime().nullable(),
    expenseId: z.string().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
// #endregion
// #region List query schema
export const RecurringExpenseListQuerySchema = z.object({
    status: RecurringExpenseStatusSchema.optional(),
    type: ExpenseTypeSchema.optional(),
});
// #endregion
//# sourceMappingURL=recurring-expenses.js.map